import React, { memo } from 'react';
import { Link } from 'react-router-dom';
import { Target, Heart, Award, Users, ArrowRight, Linkedin, BadgeCheck } from 'lucide-react';
import { SEO } from '../components/SEO';
interface PageProps {
  openGetStarted?: () => void;
}
const experts = [{
  name: 'Paul Thurston',
  role: 'Lead Resume Strategist & Executive Career Coach',
  initials: 'PT',
  image: 'https://cdn.magicpatterns.com/uploads/dxrYbCc8JGi4Ats7JtUsah/unnamed.png',
  email: 'paulthurston@hireliocareerservices.com',
  linkedin: 'https://www.linkedin.com/in/paul-thurston-7b6614a1/',
  featured: true,
  credentials: ['Certified Professional Resume Writer (CPRW)', '18+ years in executive recruiting'],
  bio: 'A former Fortune 500 hiring director, Paul has personally reviewed over 50,000 resumes. He leads our executive practice and sets the standard every Hirelio writer is trained to meet, closing the gap between exceptional talent and the systems that screen them.',
  specialties: ['Executive Resumes', 'Career Strategy', 'ATS Optimization']
}, {
  name: 'Nathan Hurwitz',
  role: 'Senior Resume Writer',
  initials: 'NH',
  image: 'https://cdn.magicpatterns.com/uploads/syYZ9Xzo1VoyZDWsR2BSTH/1595357451007.jpg',
  linkedin: 'https://www.linkedin.com/in/nathan-hurwitz/?skipRedirect=true',
  featured: false,
  credentials: ['CPRW Certified', 'Ex-Tech Recruiter'],
  bio: 'Nathan specializes in technology and engineering roles, translating complex technical work into clear, recruiter-ready achievements.',
  specialties: ['Tech & Engineering', 'LinkedIn']
}, {
  name: 'Aaron Swarts',
  role: 'Resume Writer & Career Coach',
  initials: 'AS',
  image: 'https://cdn.magicpatterns.com/uploads/pZx73Mh757JsLrVWSsL9z9/MY_NEWEST_EXPERT.jpg',
  linkedin: 'https://linkedin.com/in/aaronswarts',
  featured: false,
  credentials: ['Certified Career Coach', '12+ years coaching'],
  bio: 'Aaron writes results-driven resumes and prepares clients to walk into any interview with confidence, with a proven record of helping professionals land roles and negotiate stronger offers.',
  specialties: ['Resume Writing', 'Interview Prep', 'Negotiation']
}, {
  name: 'Laura Drouin',
  role: 'Career Branding Specialist',
  initials: 'LD',
  image: 'https://cdn.magicpatterns.com/uploads/aacu2VUtzfUMGDjSK1YnDc/WhatsApp_Image_2026-06-28_at_12.39.45_PM.jpg',
  linkedin: 'https://linkedin.com/in/elkresumewriting',
  featured: false,
  credentials: ['Certified Personal Branding Strategist', '10+ years'],
  bio: 'Laura crafts cohesive personal brands that make professionals memorable, aligning resumes, LinkedIn, and messaging into a single compelling story.',
  specialties: ['Personal Branding', 'LinkedIn', 'Storytelling']
}];
export function About({
  openGetStarted
}: PageProps) {
  const values = [{
    icon: <Target size={32} data-id="element-157" />,
    title: 'Results-Driven',
    desc: 'We measure our success by your success. Our strategies are designed to get you more interviews and better offers.'
  }, {
    icon: <Heart size={32} data-id="element-158" />,
    title: 'Human-Focused',
    desc: 'Behind every resume is a person with a unique story. We take the time to understand your individual career journey.'
  }, {
    icon: <Award size={32} data-id="element-159" />,
    title: 'Excellence',
    desc: 'We maintain the highest standards in the industry, employing only certified, experienced writers.'
  }, {
    icon: <Users size={32} data-id="element-160" />,
    title: 'Partnership',
    desc: 'We view our relationship with clients as a true partnership, working collaboratively to achieve your goals.'
  }];
  return <div className="w-full pt-20 pb-24" data-id="element-161">
      <SEO title="About Hirelio | Expert Resume Writers & Career Services" description="Hirelio is a team of certified professional resume writers and former recruiters dedicated to helping job seekers stand out and get hired faster." data-id="element-162" />
      {/* Hero */}
      <div className="bg-navy-900 text-white py-24 relative overflow-hidden" data-id="element-163">
        <div className="absolute inset-0 bg-grid opacity-20" data-id="element-164"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center" data-id="element-165">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold mb-6" data-id="element-166">
            Empowering Careers Worldwide
          </h1>
          <p className="text-lg md:text-xl text-slate-300 max-w-3xl mx-auto" data-id="element-167">
            Hirelio was founded with a simple mission: to help talented
            professionals overcome the hurdles of modern hiring and land the
            jobs they deserve.
          </p>
        </div>
      </div>

      {/* Story */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24" data-id="element-168">
        <div className="grid md:grid-cols-2 gap-16 items-center" data-id="element-169">
          <div data-id="element-170">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-navy-900 dark:text-white mb-6" data-id="element-171">
              Our Story
            </h2>
            <div className="space-y-4 text-slate-600 dark:text-slate-400 leading-relaxed" data-id="element-172">
              <p data-id="element-173">
                The job search process is broken. Brilliant candidates are
                routinely rejected by automated systems before a human ever sees
                their resume. We saw this happening time and time again, and
                decided to do something about it.
              </p>
              <p data-id="element-174">
                Hirelio brings together former recruiters, HR professionals, and
                certified resume writers who understand exactly what it takes to
                stand out in today's competitive market. We know how Applicant
                Tracking Systems work, and more importantly, we know what hiring
                managers are looking for.
              </p>
              <p data-id="element-175">
                Since our founding, we've helped over 10,000 professionals
                across 50+ industries secure better roles, negotiate higher
                salaries, and take control of their career trajectories.
              </p>
            </div>
          </div>
          <div className="relative" data-id="element-176">
            <div className="aspect-square rounded-3xl overflow-hidden bg-slate-200 dark:bg-navy-800 relative" data-id="element-177">
              {/* Placeholder for team/office image */}
              <div className="absolute inset-0 bg-navy-900/30" data-id="element-178"></div>
              <img src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80" alt="Team collaborating" className="w-full h-full object-cover" data-id="element-179" />
            </div>
            <div className="absolute -bottom-8 -left-8 bg-white dark:bg-navy-800 p-6 rounded-2xl shadow-xl border border-slate-100 dark:border-navy-700" data-id="element-180">
              <div className="text-4xl font-heading font-bold text-brand mb-1" data-id="element-181">
                10k+
              </div>
              <div className="text-sm font-medium text-slate-600 dark:text-slate-400" data-id="element-182">
                Careers Advanced
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Experts */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8" data-id="element-183">
        <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-184">
          <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-brand-50 dark:bg-brand-900/30 text-brand-600 dark:text-brand-400 text-sm font-medium mb-4" data-id="element-185">
            <Users size={14} data-id="element-186" /> Meet the Team
          </span>
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-navy-900 dark:text-white mb-4" data-id="element-187">
            The Experts Behind Your Success
          </h2>
          <p className="text-lg text-slate-600 dark:text-slate-400" data-id="element-188">
            Certified writers and former recruiters who know exactly what it
            takes to get you hired.
          </p>
        </div>

        {/* Featured expert: Paul Thurston */}
        {experts.filter(e => e.featured).map(expert => <div key={expert.name} className="bg-white dark:bg-navy-800 rounded-3xl border border-slate-200 dark:border-navy-700 shadow-card overflow-hidden mb-8 flex flex-col md:flex-row" data-id="element-189">
              <div className="md:w-72 shrink-0 bg-navy-900 p-10 flex flex-col items-center justify-center text-center relative overflow-hidden" data-id="element-190">
                <div className="absolute inset-0 bg-grid opacity-20" data-id="element-191"></div>
                {expert.image ? <img src={expert.image} alt={`${expert.name}, ${expert.role}`} className="relative z-10 w-28 h-28 rounded-full object-cover mb-5 shadow-lg shadow-brand/30 ring-2 ring-brand/40" data-id="element-192" /> : <div className="relative z-10 w-28 h-28 rounded-full bg-brand flex items-center justify-center text-white font-heading font-bold text-4xl mb-5 shadow-lg shadow-brand/30" data-id="element-193">
                    {expert.initials}
                  </div>}
                <h3 className="relative z-10 text-xl font-heading font-bold text-white" data-id="element-194">
                  {expert.name}
                </h3>
                <p className="relative z-10 text-brand-100 text-sm font-medium mb-4" data-id="element-195">
                  {expert.role}
                </p>
                {expert.linkedin && <a href={expert.linkedin} target="_blank" rel="noopener noreferrer" aria-label={`${expert.name} on LinkedIn`} className="relative z-10 w-9 h-9 rounded-full bg-navy-800 hover:bg-brand text-slate-300 hover:text-white flex items-center justify-center transition-colors" data-id="element-196">
                    <Linkedin size={18} data-id="element-197" />
                  </a>}
                {expert.email && <p className="relative z-10 mt-3 text-xs text-slate-300 break-all" data-id="element-198">
                    {expert.email}
                  </p>}
              </div>
              <div className="p-8 md:p-10 flex-1" data-id="element-199">
                <div className="flex flex-wrap gap-2 mb-5" data-id="element-200">
                  {expert.credentials.map(c => <span key={c} className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400" data-id="element-201">
                      <BadgeCheck size={14} data-id="element-202" /> {c}
                    </span>)}
                </div>
                <p className="text-slate-600 dark:text-slate-300 leading-relaxed mb-6" data-id="element-203">
                  {expert.bio}
                </p>
                <div className="flex flex-wrap gap-2" data-id="element-204">
                  {expert.specialties.map(s => <span key={s} className="text-sm font-medium px-3 py-1 rounded-lg bg-slate-100 dark:bg-navy-700 text-slate-700 dark:text-slate-200" data-id="element-205">
                      {s}
                    </span>)}
                </div>
              </div>
            </div>)}

        {/* Supporting experts */}
        <div className="grid md:grid-cols-2 gap-8" data-id="element-206">
          {experts.filter(e => !e.featured).map(expert => <div key={expert.name} className="bg-white dark:bg-navy-800 rounded-2xl border border-slate-200 dark:border-navy-700 shadow-sm p-8" data-id="element-207">
                <div className="flex items-center gap-4 mb-5" data-id="element-208">
                  {expert.image ? <img src={expert.image} alt={`${expert.name}, ${expert.role}`} className="w-16 h-16 rounded-full object-cover shrink-0 ring-2 ring-brand/20" data-id="element-209" /> : <div className="w-16 h-16 rounded-full bg-brand-50 dark:bg-navy-900 flex items-center justify-center text-brand font-heading font-bold text-xl shrink-0" data-id="element-210">
                      {expert.initials}
                    </div>}
                  <div className="flex-1" data-id="element-211">
                    <h3 className="text-lg font-heading font-bold text-navy-900 dark:text-white" data-id="element-212">
                      {expert.name}
                    </h3>
                    <p className="text-sm text-brand font-medium" data-id="element-213">
                      {expert.role}
                    </p>
                  </div>
                  {expert.linkedin && <a href={expert.linkedin} target="_blank" rel="noopener noreferrer" aria-label={`${expert.name} on LinkedIn`} className="w-9 h-9 rounded-full bg-slate-100 dark:bg-navy-700 hover:bg-brand text-slate-500 hover:text-white flex items-center justify-center transition-colors shrink-0" data-id="element-214">
                      <Linkedin size={18} data-id="element-215" />
                    </a>}
                </div>
                <p className="text-slate-600 dark:text-slate-400 text-sm leading-relaxed mb-5" data-id="element-216">
                  {expert.bio}
                </p>
                <div className="flex flex-wrap gap-2" data-id="element-217">
                  {expert.specialties.map(s => <span key={s} className="text-xs font-medium px-2.5 py-1 rounded-lg bg-slate-100 dark:bg-navy-700 text-slate-600 dark:text-slate-300" data-id="element-218">
                      {s}
                    </span>)}
                </div>
              </div>)}
        </div>
      </div>

      {/* Values */}
      <div className="bg-slate-50 dark:bg-navy-900/50 py-24" data-id="element-219">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-220">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-221">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-navy-900 dark:text-white mb-4" data-id="element-222">
              Our Core Values
            </h2>
            <p className="text-lg text-slate-600 dark:text-slate-400" data-id="element-223">
              The principles that guide everything we do.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8" data-id="element-224">
            {values.map((value, idx) => <div key={idx} className="bg-white dark:bg-navy-800 p-8 rounded-2xl shadow-sm border border-slate-100 dark:border-navy-700 text-center" data-id="element-225">
                <div className="text-brand mb-6 flex justify-center" data-id="element-226">
                  {value.icon}
                </div>
                <h3 className="text-xl font-heading font-bold text-navy-900 dark:text-white mb-3" data-id="element-227">
                  {value.title}
                </h3>
                <p className="text-slate-600 dark:text-slate-400 text-sm leading-relaxed" data-id="element-228">
                  {value.desc}
                </p>
              </div>)}
          </div>
        </div>
      </div>

      {/* CTA */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-24 text-center" data-id="element-229">
        <h2 className="text-3xl md:text-4xl font-heading font-bold text-navy-900 dark:text-white mb-6" data-id="element-230">
          Ready to write your next chapter?
        </h2>
        <p className="text-lg text-slate-600 dark:text-slate-400 mb-10" data-id="element-231">
          Let our experts help you craft a narrative that opens doors.
        </p>
        <Link to="/contact" className="bg-brand hover:bg-brand-600 text-white px-8 py-4 rounded-xl font-medium text-lg transition-all shadow-lg shadow-brand/25 inline-flex items-center justify-center gap-2 mx-auto" data-id="element-232">
          Work With Us <ArrowRight size={20} data-id="element-233" />
        </Link>
      </div>
    </div>;
}